import {useEffect, useState} from "react";
import {Link as RouterLink, useParams} from "react-router-dom";
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    Container,
    IconButton,
    Modal,
    Pagination,
    Paper,
    Tooltip,
    Typography,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CodeIcon from "@mui/icons-material/Code";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import {useTranslation} from "react-i18next";

import {useAlert} from "../context/AlertContext";
import BreadcrumbLink from "../components/BreadcrumbLink";
import AppTableHeaderCell from "../components/AppTableHeaderCell";
import AppModalBox from "../components/AppModalBox";
import {getTeacherUserAttempts} from "../api/api";
import {AdminUserAttemptsResponse, UserAttemptDetailsResponse} from "../types";

const formatDateTime = (iso?: string, locale?: string) => {
    if (!iso) return "—";

    const d = new Date(iso);

    return isNaN(d.getTime()) ? iso : d.toLocaleString(locale);
};

const ResultChip = ({solved}: { solved?: boolean }) => {
    const {t} = useTranslation();

    return (
        <Chip
            size="small"
            icon={
                solved ? (
                    <CheckCircleIcon sx={{fontSize: 16}}/>
                ) : (
                    <CancelIcon sx={{fontSize: 16}}/>
                )
            }
            label={solved ? t("userattemptslist.solved") : t("userattemptslist.notSolved")}
            color={solved ? "success" : "error"}
            sx={{
                fontWeight: 800,
                height: 24,
                fontSize: 12,
                "& .MuiChip-label": {px: 0.75},
                "& .MuiChip-icon": {ml: 0.5, mr: -0.25},
            }}
        />
    );
};

const MobileInfoRow = ({
                           icon,
                           label,
                           value,
                       }: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
}) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            minWidth: 0,
        }}
    >
        <Box
            sx={{
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                mt: 0.1,
                flexShrink: 0,
            }}
        >
            {icon}
        </Box>

        <Box sx={{minWidth: 0}}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{fontWeight: 800, display: "block"}}
            >
                {label}
            </Typography>

            <Typography
                variant="body2"
                sx={{
                    fontWeight: 700,
                    lineHeight: 1.35,
                    wordBreak: "break-word",
                }}
            >
                {value}
            </Typography>
        </Box>
    </Box>
);

const TeacherUserAttemptsPage = () => {
    const {t, i18n} = useTranslation();

    const {userId} = useParams<{ userId?: string }>();
    const safeUserId = userId ? Number(userId) : null;

    const {showAlert} = useAlert();

    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState<AdminUserAttemptsResponse | null>(null);
    const [attempts, setAttempts] = useState<UserAttemptDetailsResponse[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [codeOpen, setCodeOpen] = useState(false);
    const [selectedAttempt, setSelectedAttempt] =
        useState<UserAttemptDetailsResponse | null>(null);

    const openCode = (a: UserAttemptDetailsResponse) => {
        setSelectedAttempt(a);
        setCodeOpen(true);
    };

    const closeCode = () => {
        setCodeOpen(false);
        setSelectedAttempt(null);
    };

    const pageSize = 10;

    const loadAttempts = async () => {
        if (!safeUserId) return;

        try {
            setLoading(true);

            const res: AdminUserAttemptsResponse = await getTeacherUserAttempts(
                safeUserId,
                page - 1,
                pageSize
            );

            setMeta(res);
            setAttempts(res?.attempts?.content ?? []);
            setTotalPages(res?.attempts?.page?.totalPages ?? 1);
        } catch (e: any) {
            showAlert(e?.message ?? t("userattemptslist.errors.loadAttempts"), "error");
            setMeta(null);
            setAttempts([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAttempts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [safeUserId, page]);

    useEffect(() => {
        setPage(1);
    }, [safeUserId]);

    if (!safeUserId) {
        return (
            <Container sx={{mt: 4}}>
                <Typography color="text.secondary">
                    {t("userattemptslist.invalidUser")}
                </Typography>
            </Container>
        );
    }

    const userTitle =
        meta?.user?.fullName || meta?.user?.email || `${t("userattemptslist.user")} #${safeUserId}`;

    return (
        <Container
            maxWidth="xl"
            sx={{
                px: {xs: 1.5, sm: 2, md: 3},
            }}
        >
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small"/>} sx={{mb: 2}}>
                <BreadcrumbLink to="/my-streams">
                    {t("breadcrumb.myStreams")}
                </BreadcrumbLink>

                <BreadcrumbLink to={`/teacher/users/${safeUserId}/analytics`}>
                    {userTitle}
                </BreadcrumbLink>

                <Typography color="text.primary" variant="body2" sx={{fontWeight: 600}}>
                    {t("userattemptslist.allAttempts")}
                </Typography>
            </Breadcrumbs>

            <Box sx={{mb: 2}}>
                <Typography sx={{fontSize: {xs: 24, md: 28}, fontWeight: 900}} noWrap>
                    {userTitle}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                    {t("userattemptslist.allAttemptsHistory")}
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
                    <CircularProgress/>
                </Box>
            ) : (
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        borderRadius: 3,
                        overflow: "hidden",
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: {xs: 1.5, md: 3},
                            py: 2,
                            flexWrap: "wrap",
                            backgroundColor: theme.palette.background.paper,
                            borderBottom: `1px solid ${theme.palette.action.hover}`,
                        })}
                    >
                        <Box sx={{minWidth: 0}}>
                            <Typography sx={{fontWeight: 900}}>
                                {t("userattemptslist.attemptsHistory")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {t("userattemptslist.attemptsShownOnPage", {count: attempts.length})}
                            </Typography>
                        </Box>

                        <Box sx={{flexGrow: 1}}/>

                        <Chip
                            size="small"
                            label={t("common.pageOf", {page, totalPages})}
                            sx={(theme) => ({
                                fontWeight: 800,
                                backgroundColor: theme.palette.action.hover,
                            })}
                        />
                    </Box>

                    {attempts.length === 0 ? (
                        <Box sx={{py: 10, px: 3, textAlign: "center"}}>
                            <Typography variant="h6" sx={{mb: 1}}>
                                {t("userattemptslist.noAttemptsFound")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {t("userattemptslist.noAttemptsDescription")}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <Box sx={{display: {xs: "none", md: "block"}, width: "100%", overflowX: "auto"}}>
                                <table style={{width: "100%", borderCollapse: "separate", borderSpacing: 0}}>
                                    <thead>
                                    <tr style={{background: "#fafafa"}}>
                                        <AppTableHeaderCell>
                                            {t("userattemptslist.attemptTime")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t("userattemptslist.course")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t("userattemptslist.lesson")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t("userattemptslist.task")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t("userattemptslist.lang")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t("userattemptslist.result")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell align="center">
                                            {t("userattemptslist.actions")}
                                        </AppTableHeaderCell>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {attempts.map((a) => (
                                        <tr
                                            key={a.attemptId}
                                            style={{transition: "background-color 0.2s ease"}}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = "#fafafa";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = "transparent";
                                            }}
                                        >
                                            <td style={{padding: "18px 16px"}}>
                                                <Typography variant="body2">
                                                    {formatDateTime(a.attemptTime, i18n.language)}
                                                </Typography>
                                            </td>

                                            <td style={{padding: "18px 16px"}}>
                                                <Typography variant="body2">
                                                    {a.courseTitle || "—"}
                                                </Typography>
                                            </td>

                                            <td style={{padding: "18px 16px"}}>
                                                <Typography variant="body2">
                                                    {a.lessonTitle || "—"}
                                                </Typography>
                                            </td>

                                            <td style={{padding: "18px 16px"}}>
                                                <Typography
                                                    variant="body2"
                                                    component={RouterLink}
                                                    to={`/task-details/${a.taskId}`}
                                                    sx={(theme) => ({
                                                        color: "inherit",
                                                        textDecoration: "none",
                                                        display: "inline-block",
                                                        fontWeight: 800,
                                                        borderBottom: `1px dashed ${theme.palette.divider}`,
                                                        "&:hover": {
                                                            borderBottomColor: theme.palette.text.primary,
                                                        },
                                                    })}
                                                >
                                                    #{a.taskId} — {a.taskTitle}
                                                </Typography>
                                            </td>

                                            <td style={{padding: "18px 16px"}}>
                                                <Typography variant="body2">{a.language}</Typography>
                                            </td>

                                            <td style={{padding: "18px 16px"}}>
                                                <ResultChip solved={a.attemptResult}/>
                                            </td>

                                            <td style={{padding: "18px 16px", textAlign: "center"}}>
                                                <Box sx={{display: "inline-flex", gap: 1}}>
                                                    <Tooltip title={t("userattemptslist.viewCode")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openCode(a)}
                                                            aria-label={t("userattemptslist.viewCode")}
                                                            sx={(theme) => ({
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: "50%",
                                                                backgroundColor: theme.palette.action.hover,
                                                                color: theme.palette.text.primary,
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.action.selected,
                                                                },
                                                            })}
                                                        >
                                                            <CodeIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </Box>

                            {/* Mobile cards */}
                            <Box
                                sx={{
                                    display: {xs: "flex", md: "none"},
                                    flexDirection: "column",
                                    gap: 1.25,
                                    p: 1.5,
                                }}
                            >
                                <Box
                                    sx={(theme) => ({
                                        p: 1.5,
                                        borderRadius: 3,
                                        background:
                                            theme.palette.mode === "dark"
                                                ? "linear-gradient(135deg, rgba(30,41,59,0.95), rgba(15,23,42,0.95))"
                                                : "linear-gradient(135deg, rgba(248,250,252,1), rgba(241,245,249,1))",
                                        border: `1px solid ${theme.palette.divider}`,
                                    })}
                                >
                                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                                        <Box sx={{minWidth: 0}}>
                                            <Typography sx={{fontWeight: 900, lineHeight: 1.2}}>
                                                {t("userattemptslist.submittedSolutions")}
                                            </Typography>

                                            <Typography variant="caption" color="text.secondary">
                                                {t("userattemptslist.inspectSolutionHint")}
                                            </Typography>
                                        </Box>

                                        <Chip
                                            size="small"
                                            label={t("userattemptslist.shown", {count: attempts.length})}
                                            sx={(theme) => ({
                                                fontWeight: 800,
                                                backgroundColor: theme.palette.action.hover,
                                                flexShrink: 0,
                                            })}
                                        />
                                    </Box>
                                </Box>

                                {attempts.map((a) => (
                                    <Box
                                        key={a.attemptId}
                                        sx={(theme) => ({
                                            p: 1.5,
                                            borderRadius: 3,
                                            border: `1px solid ${theme.palette.divider}`,
                                            backgroundColor: theme.palette.background.paper,
                                            boxShadow:
                                                theme.palette.mode === "dark"
                                                    ? "0 14px 32px rgba(0,0,0,0.32)"
                                                    : "0 14px 32px rgba(15,23,42,0.07)",
                                            overflow: "hidden",
                                            position: "relative",
                                            "&:before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0,
                                                bottom: 0,
                                                left: 0,
                                                width: 4,
                                                backgroundColor: a.attemptResult
                                                    ? theme.palette.success.main
                                                    : theme.palette.error.main,
                                            },
                                        })}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                justifyContent: "space-between",
                                                gap: 1.25,
                                            }}
                                        >
                                            <Box sx={{minWidth: 0, flexGrow: 1}}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        flexWrap: "wrap",
                                                        gap: 0.75,
                                                        mb: 0.8,
                                                    }}
                                                >
                                                    <ResultChip solved={a.attemptResult}/>

                                                    <Chip
                                                        size="small"
                                                        label={a.language || "—"}
                                                        sx={(theme) => ({
                                                            height: 24,
                                                            fontSize: 12,
                                                            fontWeight: 800,
                                                            backgroundColor: theme.palette.action.hover,
                                                        })}
                                                    />
                                                </Box>

                                                <Typography
                                                    component={RouterLink}
                                                    to={`/task-details/${a.taskId}`}
                                                    sx={(theme) => ({
                                                        color: "inherit",
                                                        textDecoration: "none",
                                                        display: "inline",
                                                        fontWeight: 900,
                                                        lineHeight: 1.35,
                                                        wordBreak: "break-word",
                                                        borderBottom: `1px dashed ${theme.palette.divider}`,
                                                        "&:hover": {
                                                            borderBottomColor: theme.palette.text.primary,
                                                        },
                                                    })}
                                                >
                                                    #{a.taskId} — {a.taskTitle}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={(theme) => ({
                                                    width: 42,
                                                    height: 42,
                                                    borderRadius: "50%",
                                                    backgroundColor: theme.palette.action.hover,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                })}
                                            >
                                                <AssignmentTurnedInOutlinedIcon sx={{fontSize: 22}}/>
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={(theme) => ({
                                                mt: 1.35,
                                                p: 1.25,
                                                borderRadius: 2.5,
                                                backgroundColor: theme.palette.action.hover,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                            })}
                                        >
                                            <MobileInfoRow
                                                icon={<SchoolOutlinedIcon sx={{fontSize: 18}}/>}
                                                label={t("userattemptslist.course")}
                                                value={a.courseTitle || "—"}
                                            />

                                            <MobileInfoRow
                                                icon={<MenuBookOutlinedIcon sx={{fontSize: 18}}/>}
                                                label={t("userattemptslist.lesson")}
                                                value={a.lessonTitle || "—"}
                                            />

                                            <MobileInfoRow
                                                icon={<AccessTimeOutlinedIcon sx={{fontSize: 18}}/>}
                                                label={t("userattemptslist.attemptTime")}
                                                value={formatDateTime(a.attemptTime, i18n.language)}
                                            />
                                        </Box>

                                        <Button
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            onClick={() => openCode(a)}
                                            startIcon={<CodeIcon fontSize="small"/>}
                                            sx={{
                                                mt: 1.25,
                                                borderRadius: 2,
                                                textTransform: "none",
                                                fontWeight: 800,
                                                py: 0.85,
                                            }}
                                        >
                                            {t("userattemptslist.viewCode")}
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </Paper>
            )}

            <Box sx={{display: "flex", justifyContent: "center", mt: 3}}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    disabled={loading}
                />
            </Box>

            <Modal open={codeOpen} onClose={closeCode}>
                <AppModalBox
                    sx={{
                        p: {xs: 1.5, sm: 3, md: 4},
                        margin: "auto",
                        width: {xs: "calc(100vw - 24px)", sm: 720, md: 900},
                        maxWidth: "95vw",
                        maxHeight: "95vh",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            borderRadius: 3,
                            overflow: "hidden",
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                        })}
                    >
                        <Box
                            sx={(theme) => ({
                                px: {xs: 2, md: 3},
                                py: 2,
                                display: "flex",
                                alignItems: {xs: "stretch", sm: "center"},
                                flexDirection: {xs: "column", sm: "row"},
                                justifyContent: "space-between",
                                gap: 2,
                                borderBottom: `1px solid ${theme.palette.action.hover}`,
                                backgroundColor: theme.palette.background.paper,
                            })}
                        >
                            <Box sx={{minWidth: 0}}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    sx={{minWidth: 0, mb: 0.5, flexWrap: "wrap"}}
                                >
                                    <Typography sx={{fontSize: 18, fontWeight: 900}} noWrap>
                                        {t("userattemptslist.submittedCode")}
                                    </Typography>

                                    <ResultChip solved={selectedAttempt?.attemptResult}/>
                                </Box>

                                <Typography variant="body2" color="text.secondary">
                                    {t("userattemptslist.attemptTime")}:{" "}
                                    {formatDateTime(selectedAttempt?.attemptTime, i18n.language)}
                                </Typography>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: "block",
                                        mt: 0.25,
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {t("userattemptslist.task")}: #{selectedAttempt?.taskId} — {selectedAttempt?.taskTitle}
                                </Typography>
                            </Box>

                            <Button
                                onClick={closeCode}
                                variant="outlined"
                                sx={{
                                    textTransform: "none",
                                    width: {xs: "100%", sm: "auto"},
                                }}
                            >
                                {t("common.close")}
                            </Button>
                        </Box>

                        <Box sx={{p: {xs: 1.5, md: 3}}}>
                            <Paper
                                elevation={0}
                                sx={(theme) => ({
                                    borderRadius: 2,
                                    p: {xs: 1.5, md: 2},
                                    maxHeight: {xs: "62vh", md: "70vh"},
                                    overflow: "auto",
                                    backgroundColor:
                                        theme.palette.mode === "dark"
                                            ? "rgba(255,255,255,0.04)"
                                            : "rgba(0,0,0,0.03)",
                                })}
                            >
                                <pre
                                    style={{
                                        margin: 0,
                                        fontFamily:
                                            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                        fontSize: 13,
                                        lineHeight: 1.45,
                                        whiteSpace: "pre",
                                        overflowX: "auto",
                                    }}
                                >
                                    <code>
                                        {selectedAttempt?.codeSolution || t("userattemptslist.noCodeSubmitted")}
                                    </code>
                                </pre>
                            </Paper>
                        </Box>
                    </Paper>
                </AppModalBox>
            </Modal>
        </Container>
    );
};

export default TeacherUserAttemptsPage;